import { supabase } from '../lib/supabase';
import { ValuationData } from '../types/valuation';

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

export type CaptureTrigger =
  | 'debounce'
  | 'step_previous'
  | 'page_unload'
  | 'visibility_change'
  | 'view_results';

export type FieldSource = 'user_entered' | 'tam_accepted' | 'tam_modified';
export type FieldSourceMap = Record<string, FieldSource>;

export type TamSuggestionAction = 'accepted' | 'dismissed' | 'modified_after_accept';

interface FieldEntry {
  value: number | null;
  source: FieldSource;
}

export interface CaptureFinancialInputsParams {
  userId: string;
  sessionId: string;
  stationObjectId?: number;
  propertyAddress?: string;
  propertyCounty?: string;
  captureTrigger: CaptureTrigger;
  data: ValuationData;
  fieldSources: FieldSourceMap;
  tamResultAvailable: boolean;
}

export interface LogTamSuggestionEventParams {
  userId: string;
  sessionId: string;
  stationObjectId?: number;
  propertyAddress?: string;
  propertyCounty?: string;
  fieldName: string;
  suggestedValue: number;
  userValue: number;
  action: TamSuggestionAction;
  tamCohortTier?: string;
  tamCohortN?: number;
}

// ---------------------------------------------------------------
// Helpers: extract JSONB with per-field {value, source}
// ---------------------------------------------------------------

const REVENUE_FIELDS = [
  'annualGallons', 'fuelMargin', 'storeRevenue', 'storeMargin',
  'carWashRevenue', 'carWashMargin', 'qsrRepairBayRevenue',
  'qsrRepairBayMargin', 'totalGrossProfit',
] as const;

const EXPENSE_FIELDS = [
  'operatingExpenses', 'utilities', 'salaries', 'insurance',
  'repairsAndMaintenance', 'propertyTax', 'advertisingAndPromotion',
  'merchantFees', 'generalAndAdmin',
] as const;

function buildFieldEntries(
  data: ValuationData,
  fields: readonly string[],
  fieldSources: FieldSourceMap
): Record<string, FieldEntry> {
  const result: Record<string, FieldEntry> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataAny = data as any;
  for (const field of fields) {
    const value = dataAny[field];
    result[field] = {
      value: typeof value === 'number' ? value : null,
      source: fieldSources[field] || 'user_entered',
    };
  }
  return result;
}

function extractQsrLeaseData(data: ValuationData) {
  return {
    hasLeasedQSR: data.hasLeasedQSR ?? false,
    qsrTenantName: data.qsrTenantName ?? null,
    qsrHasDriveThru: data.qsrHasDriveThru ?? null,
    qsrLeaseStartYear: data.qsrLeaseStartYear ?? null,
    qsrLeaseEndYear: data.qsrLeaseEndYear ?? null,
    qsrIsMonthToMonth: data.qsrIsMonthToMonth ?? null,
    qsrOptionsTotal: data.qsrOptionsTotal ?? null,
    qsrCurrentMonthlyRent: data.qsrCurrentMonthlyRent ?? null,
    qsrExpenseType: data.qsrExpenseType ?? null,
    qsrAnnualLandlordExpenses: data.qsrAnnualLandlordExpenses ?? null,
  };
}

function extractSynergyData(data: ValuationData) {
  return {
    entrepreneurialSynergy: data.entrepreneurialSynergy ?? null,
  };
}

// ---------------------------------------------------------------
// Service functions (fire-and-forget)
// ---------------------------------------------------------------

/**
 * Inserts a snapshot of all financial fields into financial_input_captures.
 * Fire-and-forget: errors are logged but never thrown.
 */
export async function captureFinancialInputs(
  params: CaptureFinancialInputsParams
): Promise<void> {
  try {
    const { data: vData, fieldSources } = params;

    const { error } = await supabase
      .from('financial_input_captures')
      .insert({
        user_id: params.userId,
        session_id: params.sessionId,
        station_object_id: params.stationObjectId ?? null,
        property_address: params.propertyAddress ?? null,
        property_county: params.propertyCounty ?? null,
        capture_trigger: params.captureTrigger,
        wizard_step: 4,
        revenue_data: buildFieldEntries(vData, REVENUE_FIELDS, fieldSources),
        expense_data: buildFieldEntries(vData, EXPENSE_FIELDS, fieldSources),
        qsr_lease_data: extractQsrLeaseData(vData),
        synergy_data: extractSynergyData(vData),
        tam_result_available: params.tamResultAvailable,
      });

    if (error) {
      console.error('[DataFlywheel] Error capturing financial inputs:', error);
    } else {
      console.log('[DataFlywheel] Financial inputs captured:', params.captureTrigger);
    }
  } catch (err) {
    console.error('[DataFlywheel] Exception capturing financial inputs:', err);
  }
}

/**
 * Logs a TAM suggestion accept/dismiss/modify event.
 * Fire-and-forget: errors are logged but never thrown.
 */
export async function logTamSuggestionEvent(
  params: LogTamSuggestionEventParams
): Promise<void> {
  try {
    const { error } = await supabase
      .from('tam_suggestion_events')
      .insert({
        user_id: params.userId,
        session_id: params.sessionId,
        station_object_id: params.stationObjectId ?? null,
        property_address: params.propertyAddress ?? null,
        property_county: params.propertyCounty ?? null,
        field_name: params.fieldName,
        suggested_value: params.suggestedValue,
        user_value: params.userValue,
        action: params.action,
        tam_cohort_tier: params.tamCohortTier ?? null,
        tam_cohort_n: params.tamCohortN ?? null,
      });

    if (error) {
      console.error('[DataFlywheel] Error logging TAM suggestion event:', error);
    } else {
      console.log('[DataFlywheel] TAM event logged:', params.fieldName, params.action);
    }
  } catch (err) {
    console.error('[DataFlywheel] Exception logging TAM suggestion event:', err);
  }
}

/**
 * Marks all captures in a session as having completed payment.
 * Called after successful Stripe payment verification.
 * Fire-and-forget: errors are logged but never thrown.
 */
export async function markSessionPaymentComplete(
  sessionId: string,
  userId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('financial_input_captures')
      .update({ completed_payment: true })
      .eq('session_id', sessionId)
      .eq('user_id', userId);

    if (error) {
      console.error('[DataFlywheel] Error marking session payment complete:', error);
    } else {
      console.log('[DataFlywheel] Session marked as payment complete:', sessionId);
    }
  } catch (err) {
    console.error('[DataFlywheel] Exception marking session payment complete:', err);
  }
}
